/**
 * Puzzle generation.
 *
 * Solution first, regions second. Picking the star placement up front is what
 * makes "guaranteed solvable" structural rather than lucky: the regions are
 * built *around* a placement that already satisfies every row, column, and
 * adjacency rule, so a solution provably exists before we start testing whether
 * it is the only one.
 *
 *   1. A random legal star placement — the answer.
 *   2. Draw the tier's *given* regions around close-together pairs of stars, then
 *      pair up whatever stars are left and pick sizes for their regions that vary
 *      rather than all landing on the mean.
 *   3. Claim each pair's cells plus a path joining them, then grow the leftover
 *      cells outward. Growth never crosses a star, so every region keeps exactly
 *      its own stars, and growing from a connected seed keeps it connected.
 *   4. Repair the map until the answer is its *only* solution.
 *   5. Grade it and, while it misses the requested difficulty, nudge single
 *      cells across region borders and re-grade.
 *
 * The easier tiers are built rather than found. Step 2 draws *given* regions —
 * small shapes a player reads straight off the map — until they are worth what
 * the tier asks for. Most of them settle a star: three in a line, the 4-cell T,
 * the L in its 3×2 box, the 2×2 block with a fifth cell hanging off it. The
 * weaker ones only cross squares out, the 2×3 rectangle being the one to picture
 * — both its middles are dead on sight — and they count for half. See
 * `gradeBoard` in solver.ts for why any of this beats searching for a board the
 * ladder happens to score gently. Those regions are then frozen: steps 4 and 5
 * move cells across borders by the hundred, and a 3-cell region that gains one is
 * no longer given.
 *
 * Shape is the other half of how a board feels, and it is a tier setting too.
 * Growth picks its next cell by how much of the region it already touches, hard
 * at easy and not at all at hard, so a gentle board is drawn in blobs and an
 * expert one in the squiggles that fall out of growing at random. Every later
 * step that moves a cell keeps the same preference, and an easy board has to
 * clear a smoothness floor before it ships as easy.
 *
 * Step 4 is where the work is. A freshly grown board is nowhere near unique —
 * often hundreds of solutions — so this is a local search, not a check:
 * `driveToUnique` moves border cells and measures, descending on the solution
 * count. Two properties make that safe to do blind. Star cells never move, so
 * the intended solution survives every step; and moving a cell out of its region
 * kills every rival that starred it there, so even the un-measured moves above
 * the counting ceiling are progress.
 *
 * Symmetry is the other thing a board is built for. For most of the budget the
 * whole construction is done under a half turn of the grid: the star placement
 * is one the turn leaves alone (`symmetricPlacement`), stars are paired into
 * twin regions (`pairTwins`, `planTwinGivens`), and growth, repair and the
 * difficulty walk all move a cell and its turn together, so the map stays a
 * half turn of itself from the first cell to the last. It has to be the answer
 * that is symmetric as well as the map: a half turn takes a unique map's answer
 * to an answer of the same map, so the only answer it can have is one the turn
 * leaves alone. Twins are numbered `id` and `id ^ 1`, which is how everything
 * after growth finds a region's twin. Only once most of the budget is gone does
 * the generator fall back to unconstrained boards.
 *
 * *Nearly* symmetric is the aim, though, not exactly. The repair's last mile and
 * the walk may move a cell on its own, up to `OFF_MIRROR_ALLOWANCE` of them: a
 * board that differs from its mirror by a square or two settles into a unique
 * answer much sooner — rivals come in mirror pairs, and a lone cell only has to
 * break one — and the square that differs is information the other half does
 * not have. Boards whose opening moves come out of that difference are the
 * ones ranked highest; see `openingFromDifference` in solver.ts.
 *
 * Among boards that fit, the one handed over is the one that flows best — see
 * `flowOf` in solver.ts: how often, solving it the way a person follows a
 * thread, the next step sits right beside the last. The walk goes on a few
 * nudges past the first board that fits, and the cheaper tiers weigh a second
 * board, to have something to choose between.
 *
 * Step 5 walks on from a unique board rather than starting over, because unique
 * boards are the expensive part and most one-cell nudges keep uniqueness.
 * Generation returns the closest board it found rather than spinning forever, and
 * the UI says so when the difficulty it got is not the one that was asked for.
 */

import {
  ORTHO,
  ROW_PLACEMENTS,
  isConnected,
  perimeterDelta,
  smoothness,
  symmetryOf,
  turned,
} from './board';
import { makeRng, randomSeed, type Rng } from './rng';
import {
  GIFT_VALUE,
  GIVEN_BANDS,
  countSolutions,
  findSolutions,
  givenRegions,
  flowOf,
  gradeBoard,
  openingFromDifference,
  rateBoard,
  regionGift,
  type Gift,
} from './solver';
import { CELLS, DIFFICULTIES, SIZE, STARS, type Difficulty, type Puzzle } from './types';

export interface GenerateOptions {
  difficulty: Difficulty;
  seed?: number;
  /** Wall-clock budget. Generation returns its best board when this runs out. */
  budgetMs?: number;
  /** Called between attempts so a UI can paint. */
  onProgress?: (attempt: number) => Promise<void> | void;
}

/**
 * Search budget. A 10×10 board usually lands in one to six seconds; the rest is
 * headroom for an unlucky run, and running out means handing back a board of the
 * wrong difficulty rather than no board at all.
 */
const BUDGET_MS = 20000;
/**
 * Extra time taken, after the budget, when nothing usable was found at all.
 *
 * Producing a board of the wrong difficulty is a disappointment; producing an
 * error is a broken game.
 */
const SALVAGE_MS = 20000;
/**
 * Attempts the budget is divided into, so no one region map can consume it.
 *
 * Few enough that a slice is still long enough to finish in — slicing finer than
 * a repair run takes just guarantees every attempt is cut off mid-descent.
 */
const ATTEMPT_SLICES = 4;
/** How far apart a region's two stars should be, as a fraction of the width. */
const PAIR_REACH = 0.15;
/** How far region sizes may wander from the mean. */
const REGION_SPREAD = 0.85;
/**
 * Rounds spent repairing one region map before abandoning it for a fresh one.
 *
 * The repair search plateaus, and a stuck map is usually stuck for structural
 * reasons no number of one-cell nudges will fix — restarting beats grinding.
 */
const UNIQUE_ROUND_CAP = 80;
/** Difficulty-hunting nudges tried per unique board before rolling a new one. */
const WALK_STEPS = 12;
/**
 * Nudges taken past the first board that fits, looking for one that flows
 * better, per tier. Each costs a uniqueness check, a grade and a flow reading —
 * far less than a board at the bottom, but on expert a grade is a contradiction
 * search over the whole grid and the first fit is kept as it is.
 */
const WALK_AFTER_FIT: Record<Difficulty, number> = { easy: 4, medium: 6, hard: 4, expert: 0 };
/**
 * Fresh boards weighed per tier before the best is handed over.
 *
 * A count rather than a stretch of time, so that a seed still names one board
 * however fast the device is. More at the bottom, where a board costs a few
 * hundred milliseconds, than at the top, where it costs seconds and the walk's
 * neighbours are all the choice that is affordable.
 */
const POLISH_BOARDS: Record<Difficulty, number> = { easy: 2, medium: 2, hard: 1, expert: 1 };
/** The longest the weighing-up may add, whatever the count says. */
const POLISH_MS = 4000;
/**
 * Sizes a given region is built at.
 *
 * Three cells is two stars and the square between them. Four is the T or the S,
 * whose ends are the only seating, or the L, which places its corner star and
 * leaves two squares for the second. Five is the 2×2 block with a cell hanging
 * off it, where both seatings use that cell and so it is a star outright. Six is
 * the 2×3 rectangle — no star, but both middles are dead on sight, one star goes
 * in each short end, and the squares beside those ends go with them.
 *
 * All of them are read off the map rather than solved for — see `regionGift` —
 * and which one a pair gets is whichever fits, so a board carries a mix rather
 * than five copies of the same trick. The weaker shapes count for half a way in,
 * so a board leaning on them is drawn more of them.
 */
const GIVEN_SIZES: readonly number[] = [3, 4, 5, 6];
/**
 * Regions one board may have drawn for it rather than grown.
 *
 * Seven, so three are always left to take the sixty-odd cells the givens do not
 * use. Two regions cannot hold that much and stay a shape anybody can read.
 */
export const GIVEN_REGION_CAP = 7;
/**
 * Nudges one board may be drawn around.
 *
 * They are worth half of what an opening is worth, so a board made of them takes
 * more regions than there are to reach its band — and it would play thinner than
 * the band promises, all crosses and no stars. Two is a mix: enough that most
 * gentle boards carry one, not enough for a board to lean on them.
 */
const NUDGE_ALLOWANCE = 2;
/**
 * How far apart the two stars of a given region may sit, in steps.
 *
 * Three: far enough for the line pair (two apart, the 3-in-a-row) and the knight
 * pair (the 4-cell S, the block-plus-one), and no further. Stars four steps apart
 * need five cells just to join them, and a region that long has too many seatings
 * to give anything away.
 */
const GIVEN_REACH = 3;
/** Shapes weighed up per star pair before settling on one. */
const GIVEN_SHAPE_CAP = 8;
/**
 * How hard growth pulls toward compact regions, per tier.
 *
 * The dial from the note above: 1 always extends a region along the edge it
 * already holds most of, 0 takes any cell going. Easy is all the way over, hard
 * is left alone because the squiggles *are* part of what makes it hard — a region
 * that wanders across six rows is one the player has to keep re-tracing. Measured
 * on finished boards, that reads as `smoothness` around 0.82 at easy against 0.72
 * at hard, which is the difference between blobs and ribbons.
 */
const SMOOTH_BIAS: Record<Difficulty, number> = { easy: 1, medium: 0.6, hard: 0, expert: 0 };
/**
 * Smoothness a board must reach before it ships as this tier.
 *
 * Only the gentle tiers set a floor, and only as a backstop: growth already
 * builds blobs there, but a few hundred repair moves can chew a board back into
 * ribbons, and this is what notices. Set just under what the builder measures at,
 * so it catches the boards that came out wrong rather than most of them — a floor
 * above its own output would spend the whole budget rejecting it. Hard and expert
 * take whatever shape falls out.
 */
const SMOOTH_FLOOR: Record<Difficulty, number> = { easy: 0.8, medium: 0.74, hard: 0, expert: 0 };
/**
 * How much of the size spread a fully smoothed tier gives up.
 *
 * Half. Enough to keep the biggest region on an easy board around a fifth of the
 * grid rather than two fifths, and not so much that the free regions all land on
 * the mean, which is the arrangement `regionTargets` exists to avoid.
 */
const SPREAD_RELIEF = 0.5;

/**
 * The share of the budget spent insisting on a symmetric board.
 *
 * See the note on symmetry above. What is left goes to the unconstrained
 * generator, so a tier that cannot find a symmetric board in time still finds
 * a board.
 */
const SYMMETRIC_SHARE = 0.75;
/**
 * Cells a symmetric board may have off the mirror.
 *
 * Near symmetry is the point rather than a compromise. A board that is a half
 * turn of itself but for a square or two looks composed at a glance, and the
 * square that differs is information: its mirror image says nothing, so
 * whatever it lets the player deduce is a first move the other half does not
 * offer — which is what `openingFromDifference` rewards. Two, because past that
 * the board stops reading as symmetric at all.
 */
export const OFF_MIRROR_ALLOWANCE = 2;
/** How often the difficulty walk moves a cell on its own rather than with its turn. */
const LONE_NUDGE = 0.5;

// ── Step 1: a legal star placement ──────────────────────────────────────────

/** Random placement of two stars in every row and column, none touching. */
export function randomPlacement(rng: Rng): number[] | null {
  const colCount = new Int32Array(SIZE);
  const chosen: (readonly number[])[] = [];

  const recurse = (row: number, previous: readonly number[]): boolean => {
    if (row === SIZE) return true;
    const rowsLeft = SIZE - row;
    for (let col = 0; col < SIZE; col += 1) {
      if (STARS - (colCount[col] as number) > rowsLeft) return false;
    }
    for (const option of rng.shuffle([...ROW_PLACEMENTS])) {
      let ok = true;
      for (const col of option) {
        if ((colCount[col] as number) >= STARS) {
          ok = false;
          break;
        }
        if (previous.some((before) => Math.abs(before - col) <= 1)) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;
      for (const col of option) colCount[col] = (colCount[col] as number) + 1;
      chosen.push(option);
      if (recurse(row + 1, option)) return true;
      chosen.pop();
      for (const col of option) colCount[col] = (colCount[col] as number) - 1;
    }
    return false;
  };

  if (!recurse(0, [])) return null;
  const cells: number[] = [];
  chosen.forEach((option, row) => {
    for (const col of option) cells.push(row * SIZE + col);
  });
  return cells;
}

/**
 * A random placement that a half turn of the board leaves where it was.
 *
 * Only the top five rows are chosen; the bottom five are those turned over, so
 * row 9 is row 0 read backwards and so on. That settles every row. Columns come
 * in pairs under the turn — a star in column c of the top half is a star in
 * column 9 − c of the bottom — so column c's count is the top half's stars in c
 * and in 9 − c together, and that pair has to come to exactly two. The one
 * seam is between rows 4 and 5, which face each other across the middle and
 * must not touch.
 *
 * A symmetric map needs this: whatever a half turn does to a unique board's
 * map it does to the answer too, so the only answer such a board can have is
 * one the turn leaves alone.
 */
export function symmetricPlacement(rng: Rng): number[] | null {
  const half = SIZE / 2;
  const pairCount = new Int32Array(SIZE);
  const chosen: (readonly number[])[] = [];

  const fits = (option: readonly number[], previous: readonly number[]): boolean => {
    for (const col of option) {
      if (previous.some((before) => Math.abs(before - col) <= 1)) return false;
    }
    for (const col of option) {
      const partner = SIZE - 1 - col;
      const adding = option.filter((other) => other === col || other === partner).length;
      if ((pairCount[col] as number) + adding > STARS) return false;
    }
    return true;
  };

  const recurse = (row: number, previous: readonly number[]): boolean => {
    if (row === half) {
      const facing = previous.map((col) => SIZE - 1 - col);
      if (previous.some((col) => facing.some((other) => Math.abs(col - other) <= 1))) return false;
      return pairCount.every((count) => count === STARS);
    }
    for (const option of rng.shuffle([...ROW_PLACEMENTS])) {
      if (!fits(option, previous)) continue;
      for (const col of option) {
        pairCount[col] = (pairCount[col] as number) + 1;
        if (SIZE - 1 - col !== col)
          pairCount[SIZE - 1 - col] = (pairCount[SIZE - 1 - col] as number) + 1;
      }
      chosen.push(option);
      if (recurse(row + 1, option)) return true;
      chosen.pop();
      for (const col of option) {
        pairCount[col] = (pairCount[col] as number) - 1;
        if (SIZE - 1 - col !== col)
          pairCount[SIZE - 1 - col] = (pairCount[SIZE - 1 - col] as number) - 1;
      }
    }
    return false;
  };

  if (!recurse(0, [])) return null;
  const cells: number[] = [];
  chosen.forEach((option, row) => {
    for (const col of option) cells.push(row * SIZE + col);
  });
  return [...cells, ...cells.map(turned)];
}

// ── Steps 2 and 3: regions around the placement ─────────────────────────────

/**
 * Pairs the stars up — one pair per region.
 *
 * `reach` is how far apart a pair should be, as a fraction of the board width.
 * Pairing neighbours gives compact blobs, and a blob wide enough to hold two
 * stars comfortably lets them slide around inside it, which is exactly the
 * ambiguity the board then has to be repaired out of. Pairing distant stars
 * forces the region to stretch to reach both, and a long thin region crosses
 * many rows and columns whose own star counts then pin it down.
 *
 * It cannot be pushed all the way across the board, though: every pair is joined
 * by a path of claimed cells, and paths averaging the full width consume the grid
 * before the last few pairs get one. Measured, 0.15 wins.
 *
 * The stars of the given regions are pulled out before this is called: they are
 * paired by shape rather than by reach, and pairing them twice would hand two
 * regions the same star.
 */
export function pairStars(stars: readonly number[], rng: Rng, reach = PAIR_REACH): number[][] {
  const remaining = rng.shuffle(stars.slice());
  const pairs: number[][] = [];
  const wanted = reach * SIZE;

  while (remaining.length > 0) {
    const anchor = remaining.pop() as number;
    const pair = [anchor];
    if (remaining.length > 0) {
      const anchorRow = Math.floor(anchor / SIZE);
      const anchorCol = anchor % SIZE;
      const ranked = remaining
        .map((cell, index) => ({
          index,
          distance: Math.abs(
            Math.abs(Math.floor(cell / SIZE) - anchorRow) +
              Math.abs((cell % SIZE) - anchorCol) -
              wanted,
          ),
        }))
        .sort((a, b) => a.distance - b.distance);
      // Not strictly the closest to the target: always taking the same partner
      // produces the same pairing every time, and the region shapes with it.
      const picked = rng.pick(ranked.slice(0, Math.min(3, ranked.length)));
      pair.push(remaining[picked.index] as number);
      remaining.splice(picked.index, 1);
    }
    pairs.push(pair);
  }
  return pairs;
}

/**
 * Pairs the stars of a symmetric placement into twin regions.
 *
 * The same pairing as `pairStars`, done on the top half's stars with the bottom
 * half following: a star is paired with a partner, and the two stars a half
 * turn puts them on are paired with each other. The partner may be across the
 * middle — a star in the top half paired with one in the bottom — which is what
 * lets a pair of twins reach into each other's half rather than every region
 * stopping at the fold. Twins come out next to each other, the second the turn
 * of the first, which is the numbering `buildRegions` and the repair rely on:
 * region `id` and region `id ^ 1` are each other's turn.
 */
export function pairTwins(stars: readonly number[], rng: Rng, reach = PAIR_REACH): number[][] {
  const starSet = new Set(stars);
  const remaining = rng.shuffle(
    stars.filter((cell) => cell < CELLS / 2 && starSet.has(turned(cell))),
  );
  const pairs: number[][] = [];
  const wanted = reach * SIZE;

  while (remaining.length > 0) {
    const anchor = remaining.pop() as number;
    if (remaining.length === 0) {
      // An odd one out cannot happen on a symmetric placement — there are ten
      // top-half stars — but pair it with its own turn rather than drop it.
      pairs.push([anchor, turned(anchor)]);
      break;
    }
    // Each remaining star can be taken as itself or as its turn, whichever the
    // anchor is to be joined to; the twin region takes the other.
    const options = remaining.flatMap((cell, index) => [
      { index, partner: cell },
      { index, partner: turned(cell) },
    ]);
    const ranked = options
      .map((option) => ({
        ...option,
        distance: Math.abs(walkingDistance(anchor, option.partner) - wanted),
      }))
      .sort((a, b) => a.distance - b.distance);
    const picked = rng.pick(ranked.slice(0, Math.min(3, ranked.length)));
    remaining.splice(picked.index, 1);
    pairs.push([anchor, picked.partner]);
    pairs.push([turned(anchor), turned(picked.partner)]);
  }
  return pairs;
}

/** A region drawn to be read off the map: its exact cells, its two stars. */
export interface GivenRegion {
  pair: number[];
  cells: number[];
  /** What it hands the player, and so what it is worth toward the band. */
  gift: Gift;
}

/** What a set of drawn regions comes to, in the units `GIVEN_BANDS` is in. */
export function givenPlanValue(given: readonly GivenRegion[]): number {
  return given.reduce((total, region) => total + GIFT_VALUE[region.gift], 0);
}

/** Steps between two cells, ignoring diagonals. */
function walkingDistance(first: number, second: number): number {
  return (
    Math.abs(Math.floor(first / SIZE) - Math.floor(second / SIZE)) +
    Math.abs((first % SIZE) - (second % SIZE))
  );
}

/**
 * Every way to wrap a pair of stars in `size` cells so the region gives itself
 * away.
 *
 * A search rather than a table of shapes, because the shapes are the *answer*
 * and `regionGift` is the question — enumerate the connected sets of that size
 * containing both stars, keep the ones a player can read, and the 3-line, the T,
 * the S, the L, the block-plus-one and the 2×3 rectangle come back on their own,
 * in whichever rotation the pair happens to lie. Nothing has to be hand-listed,
 * and nothing that fails the test can slip through as a shape someone wrote out
 * wrong.
 *
 * Small enough to be exhaustive: at most four cells are added to the pair, each
 * from a frontier of a handful.
 */
function givenShapes(
  first: number,
  second: number,
  size: number,
  free: (cell: number) => boolean,
  rng: Rng,
): { cells: number[]; gift: Gift }[] {
  const found: { cells: number[]; gift: Gift }[] = [];
  const seen = new Set<string>();

  const grow = (cells: number[]): void => {
    if (found.length >= GIVEN_SHAPE_CAP) return;
    const key = [...cells].sort((a, b) => a - b).join(',');
    if (seen.has(key)) return;
    seen.add(key);
    if (cells.length === size) {
      const gift = isConnected(cells) ? regionGift(cells) : null;
      if (gift) found.push({ cells: [...cells], gift });
      return;
    }
    const frontier = new Set<number>();
    for (const cell of cells) {
      for (const neighbor of ORTHO[cell] as number[]) {
        if (!cells.includes(neighbor) && free(neighbor)) frontier.add(neighbor);
      }
    }
    for (const cell of rng.shuffle([...frontier])) {
      cells.push(cell);
      grow(cells);
      cells.pop();
    }
  };

  grow([first, second]);
  return found;
}

/**
 * Draws given regions onto a placement until they are worth `wanted`.
 *
 * Greedy over the star pairs close enough to wrap: take them in a random order,
 * try the sizes in a random order, and commit the first shape that fits in cells
 * nobody has spoken for. The target is a value rather than a count, so a run of
 * 2×3 rectangles — worth half a way in each — is answered by drawing more of
 * them, up to `GIVEN_REGION_CAP`. Which pairs a placement leaves free is luck —
 * a board whose stars all sit far apart cannot be given away however long we
 * look at it — so this reports what it managed and the caller decides whether to
 * re-roll.
 *
 * Cells already spent, and every other star, are off limits: a given region that
 * swallowed a third star would need three stars in it, and one that overlapped
 * its neighbour would not be a region at all.
 */
export function planGivens(stars: readonly number[], rng: Rng, wanted: number): GivenRegion[] {
  if (wanted <= 0) return [];
  const starCells = new Set(stars);
  const spent = new Set<number>();
  const usedStars = new Set<number>();

  const candidates: [number, number][] = [];
  for (let i = 0; i < stars.length; i += 1) {
    for (let j = i + 1; j < stars.length; j += 1) {
      const first = stars[i] as number;
      const second = stars[j] as number;
      if (walkingDistance(first, second) <= GIVEN_REACH) candidates.push([first, second]);
    }
  }

  const given: GivenRegion[] = [];
  let nudges = 0;
  for (const [first, second] of rng.shuffle(candidates)) {
    if (givenPlanValue(given) >= wanted || given.length >= GIVEN_REGION_CAP) break;
    if (usedStars.has(first) || usedStars.has(second)) continue;
    const free = (cell: number): boolean => !spent.has(cell) && !starCells.has(cell);
    for (const size of rng.shuffle([...GIVEN_SIZES])) {
      // Past the allowance the weaker shapes stop being offered, and the size
      // that only knows how to make them is passed over for the next one.
      const shapes = givenShapes(first, second, size, free, rng).filter(
        (shape) => shape.gift === 'opening' || nudges < NUDGE_ALLOWANCE,
      );
      if (shapes.length === 0) continue;
      const shape = rng.pick(shapes);
      if (shape.gift === 'nudge') nudges += 1;
      given.push({ pair: [first, second], cells: shape.cells, gift: shape.gift });
      for (const cell of shape.cells) spent.add(cell);
      usedStars.add(first);
      usedStars.add(second);
      break;
    }
  }
  return given;
}

/**
 * `planGivens` for a symmetric board: every given region drawn with its twin.
 *
 * A shape a player reads off the map reads the same turned over, so a given
 * region's twin is given too and counts for as much. The two must not overlap
 * or share a star — a pair whose second star is the first one's turn would be
 * its own twin, and the pairing leaves those to the free regions.
 */
export function planTwinGivens(stars: readonly number[], rng: Rng, wanted: number): GivenRegion[] {
  if (wanted <= 0) return [];
  const starCells = new Set(stars);
  const spent = new Set<number>();
  const usedStars = new Set<number>();

  const candidates: [number, number][] = [];
  for (const first of stars) {
    if (first >= CELLS / 2) continue;
    for (const second of stars) {
      if (second === first || second === turned(first)) continue;
      // Each twin pair once: the pair itself or its turn, whichever has its
      // first star in the top half and the smaller second star.
      if (second < first && second < CELLS / 2) continue;
      if (walkingDistance(first, second) <= GIVEN_REACH) candidates.push([first, second]);
    }
  }

  const given: GivenRegion[] = [];
  let nudges = 0;
  for (const [first, second] of rng.shuffle(candidates)) {
    if (givenPlanValue(given) >= wanted || given.length + 2 > GIVEN_REGION_CAP) break;
    const four = [first, second, turned(first), turned(second)];
    if (four.some((cell) => usedStars.has(cell))) continue;
    const free = (cell: number): boolean =>
      !spent.has(cell) && !starCells.has(cell) && !spent.has(turned(cell));
    for (const size of rng.shuffle([...GIVEN_SIZES])) {
      const shapes = givenShapes(first, second, size, free, rng).filter((shape) => {
        if (shape.gift === 'nudge' && nudges + 2 > NUDGE_ALLOWANCE) return false;
        const cells = new Set(shape.cells);
        return shape.cells.every((cell) => !cells.has(turned(cell)));
      });
      if (shapes.length === 0) continue;
      const shape = rng.pick(shapes);
      if (shape.gift === 'nudge') nudges += 2;
      const twin = shape.cells.map(turned);
      given.push({ pair: [first, second], cells: shape.cells, gift: shape.gift });
      given.push({ pair: [turned(first), turned(second)], cells: twin, gift: shape.gift });
      for (const cell of [...shape.cells, ...twin]) spent.add(cell);
      for (const cell of four) usedStars.add(cell);
      break;
    }
  }
  return given;
}

/**
 * Region sizes that sum to the whole board, spread around the mean.
 *
 * Uneven regions are what make a board solvable. The number of ways to seat two
 * stars in a region of s cells goes as s², so for a fixed total the *product*
 * across regions — roughly the size of the search a solver faces — is largest
 * when every region is the same size. Ten tidy 10-cell blobs leaves hundreds of
 * solutions; a few 5-cell regions and a few 16-cell ones usually leaves one.
 *
 * `spread` is the fraction of the mean a region's size may wander, so 0 gives
 * uniform regions and 0.85 gives sizes from a seventh of the mean to nearly
 * double it.
 *
 * `fixed` are the sizes of the given regions, which lead the list and share out
 * only what is left. They are held out of the drift too: a given region is a
 * gift at exactly the size it was drawn at, and a cell either way is a different
 * shape and usually not a gift at all.
 */
export function regionTargets(
  rng: Rng,
  spread = REGION_SPREAD,
  fixed: readonly number[] = [],
  count = SIZE,
  cells = CELLS,
): number[] {
  const minimum = 3; // two non-touching stars need a third cell to join them
  const free = count - fixed.length;
  const budget = cells - fixed.reduce((sum, size) => sum + size, 0);
  const weights = Array.from({ length: free }, () => 1 - spread + rng.next() * spread * 2);
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  const targets = weights.map((weight) => Math.max(minimum, Math.round((budget * weight) / total)));
  // Rounding and the floor both drift the sum; push the difference onto random
  // regions rather than one, so no single region absorbs it all.
  let drift = budget - targets.reduce((sum, target) => sum + target, 0);
  while (drift !== 0 && free > 0) {
    const index = rng.int(free);
    const value = targets[index] as number;
    if (drift > 0) {
      targets[index] = value + 1;
      drift -= 1;
    } else if (value > minimum) {
      targets[index] = value - 1;
      drift += 1;
    }
  }
  return [...fixed, ...targets];
}

/**
 * `regionTargets` for twin regions: half the board's worth of sizes, each used
 * twice, twins next to each other. `fixed` is the given regions' sizes, which
 * already come in twin pairs.
 */
export function twinTargets(
  rng: Rng,
  spread = REGION_SPREAD,
  fixed: readonly number[] = [],
): number[] {
  const halfFixed = fixed.filter((_, index) => index % 2 === 0);
  const half = regionTargets(rng, spread, halfFixed, SIZE / 2, CELLS / 2);
  return half.flatMap((size) => [size, size]);
}

export interface GrowOptions {
  /**
   * Exact cells for the leading regions — the givens, drawn rather than grown.
   * A region with one is never grown past it.
   */
  given?: readonly (readonly number[])[];
  /** How much growth prefers a cell it already surrounds. See `SMOOTH_BIAS`. */
  smooth?: number;
  /**
   * Grow twins together: `pairs` come from `pairTwins` (and the givens from
   * `planTwinGivens`), region `id ^ 1` is region `id` turned over, and every
   * cell one claims, the other claims the turn of.
   */
  symmetric?: boolean;
}

/**
 * Grows one region per star pair until every cell is claimed.
 *
 * Returns null when a pair's stars cannot be joined without crossing another
 * pair's territory, or when the given regions have walled off a cell nobody else
 * can reach. Both are answered by re-pairing rather than by patching: a given
 * region that took one more cell to unstick the growth would not be a gift any
 * more, which is the whole reason it was drawn.
 */
export function buildRegions(
  pairs: readonly (readonly number[])[],
  rng: Rng,
  targets: readonly number[],
  options: GrowOptions = {},
): number[] | null {
  const given = options.given ?? [];
  const smooth = options.smooth ?? 0;
  const symmetric = options.symmetric ?? false;
  const owner = new Int32Array(CELLS).fill(-1);
  const claimed: number[][] = pairs.map(() => []);

  pairs.forEach((pair, id) => {
    for (const cell of pair) {
      owner[cell] = id;
      (claimed[id] as number[]).push(cell);
    }
  });

  // The given regions come with their whole shape, stars included, and it is
  // already one connected piece — no path to find, and no room to grow into.
  for (let id = 0; id < given.length; id += 1) {
    for (const cell of given[id] as readonly number[]) {
      const holder = owner[cell] as number;
      if (holder === id) continue;
      if (holder !== -1) return null;
      owner[cell] = id;
      (claimed[id] as number[]).push(cell);
    }
  }

  const claim = (cell: number, id: number): void => {
    owner[cell] = id;
    (claimed[id] as number[]).push(cell);
  };

  // Join each remaining pair's stars with a path through unclaimed cells. A twin
  // takes the turn of its partner's path, which has to be free and must not
  // cross the path itself — a path through the middle that meets its own turn
  // would hand one cell to two regions.
  for (let id = given.length; id < pairs.length; id += symmetric ? 2 : 1) {
    const pair = pairs[id] as readonly number[];
    for (let i = 1; i < pair.length; i += 1) {
      const path = findPath(pair[0] as number, pair[i] as number, id, owner);
      if (!path) return null;
      if (symmetric) {
        const onPath = new Set(path);
        for (const cell of path) {
          if (onPath.has(turned(cell))) return null;
          const twin = owner[turned(cell)] as number;
          if (twin !== -1 && twin !== (id ^ 1)) return null;
        }
      }
      for (const cell of path) {
        if ((owner[cell] as number) !== -1) continue;
        claim(cell, id);
        if (symmetric) claim(turned(cell), id ^ 1);
      }
    }
  }

  // Grow outward, always feeding the region furthest below its target.
  let unclaimed = 0;
  for (let cell = 0; cell < CELLS; cell += 1) if ((owner[cell] as number) === -1) unclaimed += 1;

  while (unclaimed > 0) {
    const order = rng.shuffle(
      pairs
        .map((_, id) => id)
        .slice(given.length)
        .filter((id) => !symmetric || id % 2 === 0),
    );
    const deficit = (id: number): number =>
      (targets[id] ?? SIZE) - (claimed[id] as number[]).length;
    order.sort((a, b) => deficit(b) - deficit(a));
    let grew = false;
    for (const id of order) {
      // Cells on the region's edge, listed once per edge they share with it, so
      // an unbiased pick already leans slightly toward filling dents. `smooth`
      // is how often that lean becomes the rule: take the cell the region has
      // the most of a hold on, and the border comes out drawn rather than
      // ragged. At zero it is the old coin toss, and the squiggles come back.
      const frontier: number[] = [];
      for (const cell of claimed[id] as number[]) {
        for (const neighbor of ORTHO[cell] as number[]) {
          if ((owner[neighbor] as number) === -1) frontier.push(neighbor);
        }
      }
      if (frontier.length === 0) continue;
      rng.shuffle(frontier); // so equal holds are not settled by claiming order
      let target = rng.pick(frontier);
      if (rng.next() < smooth) {
        const hold = new Map<number, number>();
        for (const cell of frontier) hold.set(cell, (hold.get(cell) ?? 0) + 1);
        for (const [cell, count] of hold) {
          if (count > (hold.get(target) as number)) target = cell;
        }
      }
      if (symmetric && (owner[turned(target)] as number) !== -1) return null;
      claim(target, id);
      unclaimed -= 1;
      if (symmetric) {
        claim(turned(target), id ^ 1);
        unclaimed -= 1;
      }
      grew = true;
      break;
    }
    // Cells are left that only the given regions could reach: this map cannot be
    // finished without eating one of them. Bail rather than spin.
    if (!grew) return null;
  }

  return Array.from(owner);
}

/** Shortest path between two cells avoiding cells owned by another region. */
function findPath(from: number, to: number, id: number, owner: Int32Array): number[] | null {
  const previous = new Int32Array(CELLS).fill(-2);
  previous[from] = -1;
  const queue = [from];
  for (let head = 0; head < queue.length; head += 1) {
    const cell = queue[head] as number;
    if (cell === to) {
      const path: number[] = [];
      let step = to;
      while (step !== -1) {
        path.push(step);
        step = previous[step] as number;
      }
      return path;
    }
    for (const neighbor of ORTHO[cell] as number[]) {
      if ((previous[neighbor] as number) !== -2) continue;
      const holder = owner[neighbor] as number;
      if (holder !== -1 && holder !== id) continue;
      previous[neighbor] = cell;
      queue.push(neighbor);
    }
  }
  return null;
}

// ── Steps 4 and 5: moving border cells ──────────────────────────────────────

/**
 * Where the solution-count objective saturates.
 *
 * Counting stops here, so a board with five hundred solutions and one with
 * twenty both score 16 — but confirming a low count means exhausting the search
 * tree, and that is the expensive direction. The descent handles the flat region
 * above the ceiling separately, which is what lets the ceiling stay this low.
 */
const OBJECTIVE_CEILING = 16;
/** Moves evaluated per round; more once the board is nearly unique. */
const PROPOSALS_PER_ROUND = 10;
const PROPOSALS_LAST_MILE = 80;

/** Regions no cell may cross into or out of — the given ones, once drawn. */
const NOTHING_FROZEN: ReadonlySet<number> = new Set<number>();

/**
 * How many cells of a twin-numbered map sit off the mirror: squares whose turn
 * is not in their region's twin. Counted once per pair of squares, so one cell
 * moved on its own is one.
 */
export function offMirror(regions: readonly number[]): number {
  let count = 0;
  for (let cell = 0; cell < CELLS / 2; cell += 1) {
    if ((regions[turned(cell)] as number) !== ((regions[cell] as number) ^ 1)) count += 1;
  }
  return count;
}

/**
 * `applyMove` for a map that is allowed to be *nearly* symmetric.
 *
 * `mirrored` says whether to move the cell's turn with it; either way the result
 * is refused if it leaves more than `allowance` cells off the mirror. A mirrored
 * move on a map that is already off in one place can put it back, which is fine:
 * the allowance is a ceiling, not a target.
 */
function moveWithin(
  regions: readonly number[],
  move: { cell: number; to: number },
  frozen: ReadonlySet<number>,
  mirrored: boolean,
  allowance: number,
): number[] | null {
  const next = applyMove(regions, move, frozen, mirrored);
  if (!next || offMirror(next) > allowance) return null;
  return next;
}

/**
 * Applies a move, or returns null if it would split a region.
 *
 * On a symmetric map a move is two moves: the cell, and the cell a half turn
 * puts it on, which goes to the twin of the region the first one went to.
 * Region `id ^ 1` is region `id` turned over — see `pairTwins` — so the twin of
 * a region is one bit away. Every region the pair touches is checked, since a
 * cell moving between twins takes one cell out of each and puts one back.
 */
function applyMove(
  regions: readonly number[],
  move: { cell: number; to: number },
  frozen: ReadonlySet<number> = NOTHING_FROZEN,
  symmetric = false,
): number[] | null {
  const from = regions[move.cell] as number;
  if (frozen.has(from) || frozen.has(move.to)) return null;
  const next = regions.slice();
  next[move.cell] = move.to;
  const affected = new Set([from]);
  if (symmetric) {
    const twinCell = turned(move.cell);
    const twinFrom = regions[twinCell] as number;
    const twinTo = move.to ^ 1;
    if (frozen.has(twinFrom) || frozen.has(twinTo)) return null;
    next[twinCell] = twinTo;
    affected.add(twinFrom);
    affected.add(twinTo);
    affected.add(move.to);
  }
  for (const region of affected) {
    const rest: number[] = [];
    for (let cell = 0; cell < CELLS; cell += 1) {
      if ((next[cell] as number) === region) rest.push(cell);
    }
    if (rest.length === 0 || !isConnected(rest)) return null;
  }
  return next;
}

/**
 * Every legal one-cell move, ranked by how many sampled rivals it kills.
 *
 * `smooth` only breaks ties. Rival-killing is what gets a board to unique at all,
 * so shape never outranks it — but among moves that kill the same number, the one
 * that tidies a border is free, and there are usually several.
 */
function rankedMoves(
  regions: readonly number[],
  starCells: ReadonlySet<number>,
  rivals: readonly (readonly number[])[],
  tabu: readonly number[],
  rng: Rng,
  frozen: ReadonlySet<number> = NOTHING_FROZEN,
  smooth = 0,
): { cell: number; to: number }[] {
  const votes = new Map<number, number>();
  for (const rival of rivals) {
    for (const cell of rival) {
      if (starCells.has(cell) || tabu.includes(cell)) continue;
      if (frozen.has(regions[cell] as number)) continue;
      votes.set(cell, (votes.get(cell) ?? 0) + 1);
    }
  }
  const moves: { cell: number; to: number; score: number }[] = [];
  for (const [cell, score] of votes) {
    const from = regions[cell] as number;
    const seen = new Set<number>();
    for (const neighbor of ORTHO[cell] as number[]) {
      const to = regions[neighbor] as number;
      if (to === from || seen.has(to) || frozen.has(to)) continue;
      seen.add(to);
      moves.push({ cell, to, score });
    }
  }
  rng.shuffle(moves);
  moves.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (smooth <= 0) return 0;
    return perimeterDelta(regions, a.cell, a.to) - perimeterDelta(regions, b.cell, b.to);
  });
  return moves.map(({ cell, to }) => ({ cell, to }));
}

/**
 * A random border nudge — the difficulty walk, once uniqueness is in hand.
 *
 * `smooth` is how often the nudge has to be one that does not make the border
 * worse. Left at zero every legal move is fair game, which is what the harder
 * tiers want; turned up, the walk explores the difficulty of a board without
 * gradually unpicking the shape it was grown with.
 */
export function mutateRegions(
  regions: readonly number[],
  starCells: ReadonlySet<number>,
  rng: Rng,
  frozen: ReadonlySet<number> = NOTHING_FROZEN,
  smooth = 0,
  symmetric = false,
  allowance = 0,
): number[] | null {
  const tidy = rng.next() < smooth;
  // On a nearly symmetric map, now and then a nudge moves one cell on its own —
  // which is how a walk finds the boards whose two halves differ by a square.
  const mirrored = symmetric && !(allowance > 0 && rng.next() < LONE_NUDGE);
  let fallback: number[] | null = null;
  let fallbackCost = Infinity;
  for (const cell of rng.shuffle(regions.map((_, index) => index))) {
    if (starCells.has(cell)) continue;
    const from = regions[cell] as number;
    const targets = rng.shuffle(
      (ORTHO[cell] as number[]).map((n) => regions[n] as number).filter((to) => to !== from),
    );
    if (targets.length === 0) continue;
    const to = targets[0] as number;
    const next = symmetric
      ? moveWithin(regions, { cell, to }, frozen, mirrored, allowance)
      : applyMove(regions, { cell, to }, frozen);
    if (!next) continue;
    const cost = perimeterDelta(regions, cell, to);
    if (!tidy || cost <= 0) return next;
    // Every move on offer roughens this board; taking the least bad one beats
    // stalling the walk, which is the only thing sampling difficulty at all.
    if (cost < fallbackCost) {
      fallback = next;
      fallbackCost = cost;
    }
  }
  return fallback;
}

function sameCells(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((cell, index) => cell === b[index]);
}

/**
 * Repairs a region map until its solution is the only one, or gives up.
 *
 * A freshly grown board has hundreds of solutions, so removing them one at a
 * time never finishes. Each round samples a batch of rivals and moves the cell
 * the most of them depend on: every rival that stars that cell is invalidated at
 * once, because the cell's region loses a star while its new region gains a
 * third. The intended solution is untouched throughout — its cells are never
 * eligible to move — so the board stays solvable no matter how long this takes.
 */
export interface RepairOptions {
  trace?: (round: number, solutions: number) => void;
  roundCap?: number;
  /** Regions no cell may cross into or out of — the givens. */
  frozen?: ReadonlySet<number>;
  /** How much the repair prefers moves that tidy a border. See `SMOOTH_BIAS`. */
  smooth?: number;
  /** Move cells in twin pairs, so a symmetric map stays symmetric. */
  symmetric?: boolean;
  /**
   * On a symmetric map, how many cells may end up off the mirror. The last mile
   * tries a cell on its own when no mirrored move gets the count down.
   */
  allowance?: number;
}

export function driveToUnique(
  regions: readonly number[],
  intended: readonly number[],
  rng: Rng,
  deadline: number,
  options: RepairOptions = {},
): number[] | null {
  const {
    trace,
    roundCap = UNIQUE_ROUND_CAP,
    frozen = NOTHING_FROZEN,
    smooth = 0,
    symmetric = false,
    allowance = 0,
  } = options;
  const starCells = new Set(intended);
  // Moving a cell out of region A invalidates every rival that starred it there
  // — but the cell can be starred again in the region it landed in, so the same
  // cell keeps topping the vote and bounces between neighbours forever. The tabu
  // list is what turns that loop into progress.
  const tabu: number[] = [];
  const tabuSize = Math.max(4, Math.floor(SIZE / 2));

  let current = regions.slice();

  for (let round = 0; round < roundCap; round += 1) {
    if (Date.now() >= deadline) return null;
    const solutions = findSolutions(current, OBJECTIVE_CEILING);
    const score = solutions.length;
    if (trace) trace(round, score);
    if (score === 1) return current;
    if (score === 0) return null; // should not happen: `intended` fits

    const rivals = solutions.filter((solution) => !sameCells(solution, intended));
    const moves = rankedMoves(current, starCells, rivals, tabu, rng, frozen, smooth);
    let chosen: number[] | null = null;
    let chosenCell = -1;

    if (score >= OBJECTIVE_CEILING) {
      // Above the ceiling the objective is flat, so testing moves tells us
      // nothing. Take the highest-voted moves on faith: every rival that starred
      // one of those cells dies with the move, which is progress we cannot see.
      //
      // A batch rather than a single move, because one cell a round never gets
      // through hundreds of solutions. Two moves only compose safely when they
      // touch four different regions — the ranking was computed against the board
      // as it stands, and a region that has already changed is no longer the
      // region that ranking was about.
      const touched = new Set<number>();
      let working = current;
      const batch = Math.max(1, Math.round(SIZE / 3));
      for (const move of moves) {
        if (touched.size >= batch * (symmetric ? 4 : 2)) break;
        const from = working[move.cell] as number;
        if (touched.has(from) || touched.has(move.to)) continue;
        if (symmetric && (touched.has(from ^ 1) || touched.has(move.to ^ 1))) continue;
        const next = symmetric
          ? moveWithin(working, move, frozen, true, allowance)
          : applyMove(working, move, frozen);
        if (!next) continue;
        touched.add(from);
        touched.add(move.to);
        if (symmetric) {
          touched.add(from ^ 1);
          touched.add(move.to ^ 1);
        }
        working = next;
        tabu.push(move.cell);
      }
      while (tabu.length > tabuSize) tabu.shift();
      if (working !== current) chosen = working;
    } else {
      // Below it the count is a real gradient. Each candidate is counted with the
      // *current* score as the limit, so a move that fails to improve stops as
      // soon as it ties — which is what keeps the last mile, where confirming a
      // count means exhausting the tree, from dominating the run.
      const lastMile = score <= 4;
      const budget = lastMile ? PROPOSALS_LAST_MILE : PROPOSALS_PER_ROUND;
      // On a symmetric map the last mile also offers each cell on its own. Rival
      // answers come in mirror pairs there, and a mirrored move has to find a
      // cell that breaks both at once without making new ones; a lone cell only
      // has to break one. That is what lets a nearly symmetric board settle in
      // a few rounds where an exact one grinds — and the square it moved is
      // then the one difference between the halves that the answer turns on.
      const ways = symmetric && lastMile && allowance > 0 ? [true, false] : [symmetric];
      let tried = 0;
      for (const move of moves) {
        if (tried >= budget || Date.now() >= deadline || chosen) break;
        for (const mirrored of ways) {
          const next = symmetric
            ? moveWithin(current, move, frozen, mirrored, allowance)
            : applyMove(current, move, frozen);
          if (!next) continue;
          tried += 1;
          if (findSolutions(next, score).length < score) {
            chosen = next;
            chosenCell = move.cell;
            break;
          }
        }
      }
    }

    if (!chosen) {
      // Nothing on offer improved things: step sideways at random and let the
      // tabu list stop the next round undoing it.
      const kick = mutateRegions(current, starCells, rng, frozen, smooth, symmetric, allowance);
      if (!kick) return null;
      chosen = kick;
    }
    if (chosenCell >= 0) {
      tabu.push(chosenCell);
      while (tabu.length > tabuSize) tabu.shift();
    }
    current = chosen;
  }
  return null;
}

// ── Driver ──────────────────────────────────────────────────────────────────

function distance(a: Difficulty, b: Difficulty): number {
  return Math.abs(DIFFICULTIES.indexOf(a) - DIFFICULTIES.indexOf(b));
}

interface Candidate {
  regions: number[];
  solution: number[];
  difficulty: Difficulty;
  /** How blobby the regions came out — see `SMOOTH_FLOOR`. */
  smoothness: number;
}

/**
 * How a board ranks against others that fit the same tier.
 *
 * Symmetry counts double. It is what makes a board look composed before a
 * single mark goes on it; flow is what makes it feel composed while it is
 * played, and among boards that are equally symmetric it decides.
 */
function merit(candidate: Candidate): number {
  return (
    2 * symmetryOf(candidate.regions) +
    (flowOf(candidate.regions) ?? 0) +
    OPENING_WEIGHT * openingFromDifference(candidate.regions)
  );
}

/**
 * What a first move coming out of the difference between the halves is worth,
 * against flow's 0 to 1 and symmetry's 2 per unit.
 *
 * Enough that a nearly symmetric board whose opening comes from its one odd
 * square outranks an exact one with the same flow, and more than the two cells
 * of symmetry it gave up (2 × 2/100): that trade is the board the generator is
 * looking for.
 */
const OPENING_WEIGHT = 0.6;

/**
 * Builds a puzzle and, budget permitting, one of the requested difficulty.
 *
 * Async only so the caller can keep a browser tab responsive: `onProgress` is
 * awaited between attempts.
 */
export async function generatePuzzle(options: GenerateOptions): Promise<Puzzle> {
  const { difficulty } = options;
  const seed = options.seed ?? randomSeed();
  const rng = makeRng(seed);
  const budgetMs = options.budgetMs ?? BUDGET_MS;
  const deadline = Date.now() + budgetMs;

  let best: Candidate | null = null;
  let attempt = 0;

  /** Whether a board is the thing that was asked for, shape included. */
  const fits = (candidate: Candidate): boolean =>
    candidate.difficulty === difficulty && candidate.smoothness >= SMOOTH_FLOOR[difficulty];

  /** Which of two near-misses to keep: the right tier first, then the tidier. */
  const better = (candidate: Candidate, than: Candidate): boolean => {
    const gap = distance(candidate.difficulty, difficulty);
    const held = distance(than.difficulty, difficulty);
    if (gap !== held) return gap < held;
    return candidate.smoothness > than.smoothness;
  };

  /**
   * One unique, rateable board, or null if this attempt came to nothing.
   *
   * `relax` accepts a unique board the technique ladder could not finish. That is
   * not a broken puzzle — the solution is still provably the only one, and still
   * reachable by logic — only one that needs deeper case analysis than the ladder
   * does, so it goes out labelled expert. Reserved for the salvage pass, where
   * the alternative is handing the player nothing.
   */
  const attemptOnce = async (
    until: number,
    relax = false,
    symmetric = false,
  ): Promise<Candidate | null> => {
    attempt += 1;
    if (options.onProgress) await options.onProgress(attempt);

    const placement = symmetric ? symmetricPlacement(rng) : randomPlacement(rng);
    if (!placement) throw new Error('no legal star placement exists');
    const starCells = new Set(placement);

    const band = GIVEN_BANDS[difficulty];
    const wantGiven = band[0] + rng.int(band[1] - band[0] + 1);
    const smooth = SMOOTH_BIAS[difficulty];

    let grown: number[] | null = null;
    let built = 0;
    for (let tries = 0; tries < 12 && !grown; tries += 1) {
      const given = symmetric
        ? planTwinGivens(placement, rng, wantGiven)
        : planGivens(placement, rng, wantGiven);
      // Short of the way in this difficulty wants: re-roll rather than grow.
      // Which stars a placement leaves close enough to wrap is luck, and another
      // shuffle costs nothing next to a grown board — but a placement with too
      // few going spare is not worth all twelve tries either.
      if (givenPlanValue(given) < wantGiven && tries < 8) continue;
      const spoken = new Set(given.flatMap((region) => region.pair));
      const rest = placement.filter((cell) => !spoken.has(cell));
      const pairs = [
        ...given.map((region) => region.pair),
        ...(symmetric ? pairTwins(rest, rng, PAIR_REACH) : pairStars(rest, rng, PAIR_REACH)),
      ];
      const shapes = given.map((region) => region.cells);
      // The givens have already made the sizes lopsided — five 4-cell regions is
      // as uneven as a board gets — so the free ones need less of the spread that
      // buys uniqueness, and evening them out keeps a tier that is meant to look
      // tidy from handing one region a third of the grid.
      const spread = REGION_SPREAD * (1 - SPREAD_RELIEF * smooth);
      const sizes = shapes.map((cells) => cells.length);
      const targets = symmetric
        ? twinTargets(rng, spread, sizes)
        : regionTargets(rng, spread, sizes);
      const map = buildRegions(pairs, rng, targets, { given: shapes, smooth, symmetric });
      if (!map) continue;
      grown = map;
      built = given.length;
    }
    if (!grown) return null;

    // Only the ones that actually came out given: a shape is drawn against an
    // empty board, and a region that ends up boxed in against the edge can read
    // differently once its neighbours exist. Freezing a region that is not a
    // gift would cost the repair search moves for nothing.
    const frozen = new Set(givenRegions(grown).filter((region) => region < built));

    const allowance = symmetric ? OFF_MIRROR_ALLOWANCE : 0;
    let current = driveToUnique(grown, placement, rng, until, {
      frozen,
      smooth,
      symmetric,
      allowance,
    });
    if (!current) return null;

    const unrateable: Candidate = {
      regions: current.slice(),
      solution: placement.slice(),
      difficulty: 'expert',
      smoothness: smoothness(current),
    };

    // Unique boards are the expensive part, so try the difficulty from here as
    // well: a nudge that keeps uniqueness is another sample for much less than a
    // fresh board costs. Bounded, though — a board's neighbourhood tends to rate
    // like the board, and past a dozen nudges a new board is the better bet.
    let found: Candidate | null = null;
    let fitted: { candidate: Candidate; merit: number } | null = null;
    let fitsLeft = WALK_STEPS;
    let rating = rateBoard(current);
    for (let walked = 0; walked < WALK_STEPS && Date.now() < until; walked += 1) {
      const graded = gradeBoard(current, rating);
      if (graded) {
        const candidate: Candidate = {
          regions: current.slice(),
          solution: placement.slice(),
          difficulty: graded,
          smoothness: smoothness(current),
        };
        // Shape is the one thing the grade does not carry, so `fits` checks it
        // here rather than `gradeBoard` folding it in: a board that plays easy
        // but was chewed into ribbons on the way is still an easy board, just
        // not one to hand over while there is budget left to find a tidier one.
        // Keeping it as `found` is what stops that preference costing a player
        // their board.
        if (fits(candidate)) {
          // The first fit is a puzzle. A few more nudges are cheap next to a
          // fresh board and often find a neighbour that fits as well and flows
          // better, so the walk goes on a little and keeps the best of them.
          const worth = merit(candidate);
          if (!fitted || worth > fitted.merit) fitted = { candidate, merit: worth };
          fitsLeft = Math.min(fitsLeft, WALK_AFTER_FIT[difficulty]);
        } else if (!found || better(candidate, found)) {
          found = candidate;
        }
      }
      if (fitted && fitsLeft-- <= 0) break;
      if (walked % 8 === 0 && options.onProgress) await options.onProgress(attempt);

      const next = mutateRegions(current, starCells, rng, frozen, smooth, symmetric, allowance);
      if (!next) break;
      if (countSolutions(next, 2) !== 1) continue;
      current = next;
      rating = rateBoard(current);
    }
    if (fitted) return fitted.candidate;
    return found ?? (relax ? unrateable : null);
  };

  // Each attempt gets a slice rather than whatever is left, so one unlucky region
  // map cannot eat the budget and leave no successor — restarting is what makes
  // the search work.
  const slice = Math.max(2000, Math.floor(budgetMs / ATTEMPT_SLICES));
  const nextSlice = (until: number): number => Math.min(until, Date.now() + slice);

  const symmetricUntil = Date.now() + budgetMs * SYMMETRIC_SHARE;
  let pick: { candidate: Candidate; merit: number } | null = null;
  let weighed = 0;
  let polishUntil = deadline;
  while (Date.now() < deadline) {
    if (pick && (weighed >= POLISH_BOARDS[difficulty] || Date.now() > polishUntil)) break;
    const symmetric = Date.now() < symmetricUntil;
    const candidate = await attemptOnce(nextSlice(deadline), false, symmetric);
    if (!candidate) continue;
    if (fits(candidate)) {
      const worth = merit(candidate);
      weighed += 1;
      if (!pick) polishUntil = Math.min(deadline, Date.now() + POLISH_MS);
      if (!pick || worth > pick.merit) pick = { candidate, merit: worth };
      continue;
    }
    if (!best || better(candidate, best)) best = candidate;
  }

  if (pick) return finish(pick.candidate, seed);
  if (best) return finish(best, seed);

  // The budget went by without a single usable board: a slow device or an
  // unlucky run, not an impossible request. Keep going on borrowed time.
  const salvageUntil = Date.now() + SALVAGE_MS;
  while (Date.now() < salvageUntil) {
    const candidate = await attemptOnce(nextSlice(salvageUntil), true);
    if (candidate) return finish(candidate, seed);
  }
  throw new Error('could not generate a puzzle in the time allowed');
}

function finish(candidate: Candidate, seed: number): Puzzle {
  return {
    regions: candidate.regions,
    solution: candidate.solution,
    difficulty: candidate.difficulty,
    seed,
  };
}
